import { gql, useMutation, useQuery } from '@apollo/client';
import { toast } from 'sonner';
import { GET_ALL_OOPS } from '../graphql/queries';
import { getMongoDBCompassFormat } from './useMockFinancialData';

// Import test application mutations
const CREATE_TEST_CSAW_PERMIT = gql`
   mutation CreateTestCSAWPermit($preset: CSAWPresetInput) {
      createTestCSAWPermit(preset: $preset) {
         id
         applicationNumber
         status
         currentStage
      }
   }
`;

const CREATE_TEST_COV_PERMIT = gql`
   mutation CreateTestCOVPermit($preset: COVPresetInput) {
      createTestCOVPermit(preset: $preset) {
         id
         applicationNumber
         status
         currentStage
      }
   }
`;

const CREATE_TEST_PTPR_PERMIT = gql`
   mutation CreateTestPTPRPermit($preset: PTPRPresetInput) {
      createTestPTPRPermit(preset: $preset) {
         id
         applicationNumber
         status
         currentStage
      }
   }
`;

const CREATE_TEST_PLTCP_PERMIT = gql`
   mutation CreateTestPLTCPPermit($preset: PLTCPPresetInput) {
      createTestPLTCPPermit(preset: $preset) {
         id
         applicationNumber
         status
         currentStage
      }
   }
`;

const CREATE_TEST_PLTP_PERMIT = gql`
   mutation CreateTestPLTPPermit($preset: PLTPPresetInput) {
      createTestPLTPPermit(preset: $preset) {
         id
         applicationNumber
         status
         currentStage
      }
   }
`;

const CREATE_TEST_TCEBP_PERMIT = gql`
   mutation CreateTestTCEBPPermit($preset: TCEBPPresetInput) {
      createTestTCEBPPermit(preset: $preset) {
         id
         applicationNumber
         status
         currentStage
      }
   }
`;

const CREATE_TEST_OOP = gql`
   mutation CreateTestOOP($applicationNumber: String!) {
      createTestOOP(applicationNumber: $applicationNumber) {
         id
         billNo
         applicationNumber
         namePayee
         totalAmount
         OOPstatus
         OOPSignedByTwoSignatories
         trackingNo
      }
   }
`;

export const useTestApplications = () => {
   const [createTestCSAWPermit] = useMutation(CREATE_TEST_CSAW_PERMIT);
   const [createTestCOVPermit] = useMutation(CREATE_TEST_COV_PERMIT);
   const [createTestPTPRPermit] = useMutation(CREATE_TEST_PTPR_PERMIT);
   const [createTestPLTCPPermit] = useMutation(CREATE_TEST_PLTCP_PERMIT);
   const [createTestPLTPPermit] = useMutation(CREATE_TEST_PLTP_PERMIT);
   const [createTestTCEBPPermit] = useMutation(CREATE_TEST_TCEBP_PERMIT);
   const [createTestOOP] = useMutation(CREATE_TEST_OOP);

   const { refetch: refetchOOPs } = useQuery(GET_ALL_OOPS);

   const handleCreateTestCSAW = async () => {
      try {
         let preset = null;
         const storedPreset = localStorage.getItem('csawPreset');

         if (storedPreset) {
            try {
               const parsedPreset = JSON.parse(storedPreset);
               console.log('Parsed preset:', parsedPreset);

               // Remove the files field if it exists
               const { files, ...presetData } = parsedPreset;
               preset = presetData;
               console.log('Processed preset:', preset);
            } catch (parseError) {
               console.error('Error parsing preset:', parseError);
               toast.error('Error loading preset data');
               return;
            }
         }

         console.log('Sending mutation with preset:', preset);

         const { data } = await createTestCSAWPermit({
            variables: { preset }
         });

         if (data?.createTestCSAWPermit) {
            toast.success('Test CSAW application created!', {
               description: `Application #${data.createTestCSAWPermit.applicationNumber} created successfully`
            });
         } else {
            throw new Error('No data returned from mutation');
         }
      } catch (error) {
         console.error('Error creating test CSAW application:', error);
         toast.error(`Failed to create test CSAW application: ${error.message}`);
      }
   };

   const handleCreateTestCOV = async () => {
      try {
         let preset = null;
         const storedPreset = localStorage.getItem('covPreset');

         if (storedPreset) {
            try {
               const parsedPreset = JSON.parse(storedPreset);
               console.log('Parsed COV preset:', parsedPreset);

               // Remove the files field if it exists
               const { files, ...presetData } = parsedPreset;
               preset = presetData;
               console.log('Processed COV preset:', preset);
            } catch (parseError) {
               console.error('Error parsing COV preset:', parseError);
               toast.error('Error loading COV preset data');
               return;
            }
         }

         console.log('Sending COV mutation with preset:', preset);

         const { data } = await createTestCOVPermit({
            variables: { preset }
         });

         if (data?.createTestCOVPermit) {
            toast.success('Test COV application created!', {
               description: `Application #${data.createTestCOVPermit.applicationNumber} created successfully`
            });
         } else {
            throw new Error('No data returned from mutation');
         }
      } catch (error) {
         console.error('Error creating test COV application:', error);
         toast.error(`Failed to create test COV application: ${error.message}`);
      }
   };

   const handleCreateTestPTPR = async () => {
      try {
         let preset = null;
         const storedPreset = localStorage.getItem('ptprPreset');

         if (storedPreset) {
            try {
               const parsedPreset = JSON.parse(storedPreset);
               console.log('Parsed PTPR preset:', parsedPreset);

               // Remove the files field if it exists
               const { files, ...presetData } = parsedPreset;
               preset = presetData;
               console.log('Processed PTPR preset:', preset);
            } catch (parseError) {
               console.error('Error parsing PTPR preset:', parseError);
               toast.error('Error loading PTPR preset data');
               return;
            }
         }

         console.log('Sending mutation with preset:', preset);

         const { data } = await createTestPTPRPermit({
            variables: { preset }
         });

         if (data?.createTestPTPRPermit) {
            toast.success('Test PTPR application created!', {
               description: `Application #${data.createTestPTPRPermit.applicationNumber} created successfully`
            });
         } else {
            throw new Error('No data returned from mutation');
            // localStorage.removeItem('pltpPreset');
         }
      } catch (error) {
         console.error('Error creating test PTPR application:', error);
         toast.error(`Failed to create test PTPR application: ${error.message}`);
      }
   };

   const handleCreateTestPLTCP = async () => {
      try {
         let preset = null;
         const storedPreset = localStorage.getItem('pltcpPreset');

         if (storedPreset) {
            try {
               const parsedPreset = JSON.parse(storedPreset);
               console.log('Parsed PLTCP preset:', parsedPreset);
               console.log('Request type from preset:', parsedPreset.requestType);

               const { files, ...presetData } = parsedPreset;
               preset = presetData;
               console.log('Processed PLTCP preset:', preset);
            } catch (parseError) {
               console.error('Error parsing PLTCP preset:', parseError);
               toast.error('Error loading PLTCP preset data');
               return;
            }
         }

         const { data } = await createTestPLTCPPermit({
            variables: { preset }
         });

         if (data?.createTestPLTCPPermit) {
            toast.success('Test PLTCP application created!', {
               description: `Application #${data.createTestPLTCPPermit.applicationNumber} created successfully`
            });
         } else {
            throw new Error('No data returned from mutation');
         }
      } catch (error) {
         console.error('Error creating test PLTCP application:', error);
         toast.error(`Failed to create test PLTCP application: ${error.message}`);
      }
   };

   const handleCreateTestPLTP = async () => {
      try {
         let preset = null;
         const storedPreset = localStorage.getItem('pltpPreset');

         if (storedPreset) {
            try {
               const parsedPreset = JSON.parse(storedPreset);
               console.log('Parsed PLTP preset:', parsedPreset);
               console.log('Request type from preset:', parsedPreset.requestType);

               const { files, ...presetData } = parsedPreset;
               preset = presetData;
               console.log('Processed PLTP preset:', preset);
            } catch (parseError) {
               console.error('Error parsing PLTP preset:', parseError);
               toast.error('Error loading PLTP preset data');
               return;
            }
         }

         const { data } = await createTestPLTPPermit({
            variables: { preset }
         });

         if (data?.createTestPLTPPermit) {
            toast.success('Test PLTP application created!', {
               description: `Application #${data.createTestPLTPPermit.applicationNumber} created successfully`
            });
         } else {
            throw new Error('No data returned from mutation');
         }
      } catch (error) {
         console.error('Error creating test PLTP application:', error);
         toast.error(`Failed to create test PLTP application: ${error.message}`);
      }
   };

   const handleCreateTestTCEBP = async () => {
      try {
         let preset = null;
         const storedPreset = localStorage.getItem('tcebpPreset');

         if (storedPreset) {
            try {
               const parsedPreset = JSON.parse(storedPreset);
               console.log('Parsed TCEBP preset:', parsedPreset);

               const { files, ...presetData } = parsedPreset;
               preset = presetData;
               console.log('Processed TCEBP preset:', preset);
            } catch (parseError) {
               console.error('Error parsing TCEBP preset:', parseError);
               toast.error('Error loading TCEBP preset data');
               return;
            }
         }

         const { data } = await createTestTCEBPPermit({
            variables: { preset }
         });

         if (data?.createTestTCEBPPermit) {
            toast.success('Test TCEBP application created!', {
               description: `Application #${data.createTestTCEBPPermit.applicationNumber} created successfully`
            });
         } else {
            throw new Error('No data returned from mutation');
         }
      } catch (error) {
         console.error('Error creating test TCEBP application:', error);
         toast.error(`Failed to create test TCEBP application: ${error.message}`);
      }
   };

   const handleCreateTestOOP = async (applicationNumber) => {
      try {
         const { data } = await createTestOOP({
            variables: { applicationNumber }
         });

         if (data?.createTestOOP) {
            toast.success('Test OOP created successfully', {
               description: `OOP #${data.createTestOOP.billNo} created for application ${applicationNumber}`
            });
         }
      } catch (error) {
         console.error('Error creating test OOP:', error);
         toast.error('Failed to create test OOP');
      }
   };

   // Function to generate and download financial data JSON
   const exportFinancialDataJSON = () => {
      try {
         const jsonData = getMongoDBCompassFormat();
         const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
         const url = URL.createObjectURL(blob);

         // Create download link
         const a = document.createElement('a');
         a.href = url;
         a.download = 'officialreceipts.json';
         document.body.appendChild(a);
         a.click();

         // Cleanup
         document.body.removeChild(a);
         URL.revokeObjectURL(url);

         toast.success('Financial data JSON exported', {
            description: 'You can now import this file into MongoDB Compass'
         });
      } catch (error) {
         toast.error(`Failed to export financial data: ${error.message}`);
         console.error('Error exporting financial data:', error);
      }
   };

   return {
      handleCreateTestCSAW,
      handleCreateTestCOV,
      handleCreateTestPTPR,
      handleCreateTestPLTCP,
      handleCreateTestPLTP,
      handleCreateTestTCEBP,
      handleCreateTestOOP,
      refetchOOPs,
      exportFinancialDataJSON
   };
};